import { useEffect, useState } from 'react';
import client from '../api/client';
import { User } from '../types';

export interface UserNameInfo {
  id: number;
  name: string;
  username: string | null;
  full_name: string | null;
}

const cache = new Map<number, UserNameInfo>();
const inflight = new Map<number, Promise<UserNameInfo | null>>();

const fetchOne = (id: number): Promise<UserNameInfo | null> => {
  if (cache.has(id)) {
    return Promise.resolve(cache.get(id) ?? null);
  }
  const existing = inflight.get(id);
  if (existing) return existing;

  const p = client
    .get<User>(`/users/${id}`)
    .then((r) => {
      const info: UserNameInfo = {
        id: r.data.id,
        name: r.data.name,
        username: r.data.username,
        full_name: r.data.full_name,
      };
      cache.set(id, info);
      return info;
    })
    .catch(() => null)
    .finally(() => {
      inflight.delete(id);
    });

  inflight.set(id, p);
  return p;
};

/**
 * Resolve display names for a list of user IDs.
 * Returns a stable map keyed by user id. Missing entries mean "still loading"
 * or "not found"; callers should fall back to `#${id}`.
 */
export const useUserNames = (ids: Array<number | null | undefined>): Record<number, UserNameInfo> => {
  const [names, setNames] = useState<Record<number, UserNameInfo>>(() => {
    const out: Record<number, UserNameInfo> = {};
    for (const id of ids) {
      if (typeof id === 'number' && cache.has(id)) {
        out[id] = cache.get(id) as UserNameInfo;
      }
    }
    return out;
  });

  const uniqueKey = Array.from(
    new Set(ids.filter((v): v is number => typeof v === 'number' && v > 0)),
  )
    .sort((a, b) => a - b)
    .join(',');

  useEffect(() => {
    const wanted = uniqueKey ? uniqueKey.split(',').map((s) => parseInt(s, 10)) : [];
    if (wanted.length === 0) return;

    let cancelled = false;
    const missing = wanted.filter((id) => !cache.has(id));

    if (missing.length === 0) {
      const out: Record<number, UserNameInfo> = {};
      for (const id of wanted) {
        const v = cache.get(id);
        if (v) out[id] = v;
      }
      setNames(out);
      return;
    }

    Promise.all(missing.map((id) => fetchOne(id))).then(() => {
      if (cancelled) return;
      const out: Record<number, UserNameInfo> = {};
      for (const id of wanted) {
        const v = cache.get(id);
        if (v) out[id] = v;
      }
      setNames(out);
    });

    return () => {
      cancelled = true;
    };
  }, [uniqueKey]);

  return names;
};

/**
 * Best-effort display label for a user.
 * Prefers `@username`, then `full_name`, then `name`, with the numeric id appended
 * for disambiguation. Falls back to `#id` if nothing has been resolved yet.
 */
export const formatUserLabel = (
  id: number | null | undefined,
  names: Record<number, UserNameInfo>,
): string => {
  if (typeof id !== 'number' || id <= 0) return '—';
  const info = names[id];
  if (!info) return `#${id}`;
  if (info.username) return `@${info.username}`;
  if (info.full_name) return info.full_name;
  if (info.name) return info.name;
  return `#${id}`;
};
